import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Star, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, ScriptKicker, FilipinoSun } from "../components/Reveal";
import { MENU, FEATURED_DISHES } from "../lib/data";
import { usePageMeta } from "../hooks/usePageMeta";

export default function Menu() {
  usePageMeta({
    title: "Filipino Menu & Party Trays | Order Online | San Diego",
    description:
      "Browse Ateng K Sarap's Filipino catering menu — pansit, lechon belly, kare-kare, buffet & dessert trays. Order online for your next San Diego event.",
    path: "/menu",
  });
  const categories = Object.keys(MENU);

  return (
    <div data-testid="menu-page" className="overflow-hidden">
      <section className="relative pt-20 pb-12 lg:pt-32 lg:pb-16 bg-background">
        <div className="absolute top-16 right-10 text-secondary/15 hidden lg:block"><FilipinoSun size={120} /></div>
        <div className="container-eds relative">
          <div className="overline flex items-center gap-3"><span className="w-8 h-px bg-secondary" /> Our Menu</div>
          <ScriptKicker className="block mt-3">Sarap! From our kitchen to yours</ScriptKicker>
          <h1 className="mt-2 font-display text-5xl sm:text-6xl lg:text-7xl font-medium text-foreground leading-[0.98] tracking-tight max-w-4xl">
            Filipino <span className="italic text-secondary">classics</span>,
            <br />
            crafted for your <span className="italic text-primary">table</span>.
          </h1>
          <p className="mt-6 text-muted-foreground text-base lg:text-lg max-w-2xl">
            Browse signature dishes by category. All catering menus are tailored to
            your event size and budget — reach out for a custom quote.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-background">
        <div className="container-eds">
          <Tabs defaultValue={categories[0]} className="w-full">
            <div className="overflow-x-auto no-scrollbar -mx-5 px-5">
              <TabsList
                data-testid="menu-tabs"
                className="bg-card border border-border h-auto p-1.5 inline-flex w-auto rounded-full"
              >
                {categories.map((c) => (
                  <TabsTrigger
                    key={c}
                    value={c}
                    data-testid={`menu-tab-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    className="data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground text-foreground rounded-full px-5 py-2.5 text-xs lg:text-sm font-semibold whitespace-nowrap"
                  >
                    {c}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {categories.map((c) => (
              <TabsContent key={c} value={c} className="mt-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
                  <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
                    <Reveal>
                      <div className="overline">Category</div>
                      <ScriptKicker className="block mt-2">{categoryKicker(c)}</ScriptKicker>
                      <h2 className="mt-2 font-display text-4xl lg:text-5xl font-medium text-foreground leading-tight">
                        {c}
                      </h2>
                      <p className="mt-5 text-muted-foreground leading-relaxed">
                        {categoryBlurb(c)}
                      </p>
                      <Link
                        to="/contact"
                        data-testid={`menu-inquire-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        className="btn-outline mt-8"
                      >
                        Inquire <ArrowRight className="w-4 h-4" />
                      </Link>
                    </Reveal>
                  </div>
                  <div className="lg:col-span-8 space-y-5">
                    {MENU[c].map((item, i) => (
                      <Reveal key={item.name} delay={i * 0.04}>
                        <div
                          data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className="group bg-card hover:bg-foreground hover:text-background rounded-3xl p-6 lg:p-7 transition-all duration-500 border border-border"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 flex-wrap">
                                <h3 className="font-display text-2xl lg:text-3xl font-semibold">{item.name}</h3>
                                {item.featured && (
                                  <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase bg-accent text-accent-foreground px-2.5 py-1 font-bold rounded-full">
                                    <Star className="w-2.5 h-2.5 fill-current" /> Top Favorite
                                  </span>
                                )}
                              </div>
                              <p className="mt-2 text-sm lg:text-base text-muted-foreground group-hover:text-background/80">
                                {item.desc}
                              </p>
                            </div>
                            <div className="font-display text-3xl text-accent/40 group-hover:text-accent transition-colors">
                              {String(i + 1).padStart(2, "0")}
                            </div>
                          </div>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Top favorites strip */}
      <section className="py-16 lg:py-24 bg-card">
        <div className="container-eds">
          <Reveal>
            <div className="text-center mx-auto max-w-3xl">
              <div className="overline flex items-center justify-center gap-3"><span className="w-8 h-px bg-secondary" /> Top Favorites <span className="w-8 h-px bg-secondary" /></div>
              <ScriptKicker className="block mt-3">Always the first to disappear</ScriptKicker>
              <h2 className="mt-2 font-display text-4xl lg:text-5xl xl:text-6xl font-medium text-foreground leading-tight">
                The dishes guests <span className="italic text-secondary">remember</span>.
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {FEATURED_DISHES.map((d, i) => (
              <Reveal key={d.name} delay={i * 0.06}>
                <div className="relative overflow-hidden aspect-square rounded-3xl group">
                  <img src={d.image} alt={d.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-5 lg:p-6 text-background">
                    <h3 className="font-display text-xl lg:text-2xl font-semibold">{d.name}</h3>
                    <p className="font-script text-lg text-accent mt-0.5">{d.tagline}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function categoryKicker(c) {
  const map = {
    Appetizers: "Start with a smile",
    "Main Dishes": "The heart of the feast",
    "Noodles & Rice": "Long noodles, long life",
    "Party Trays": "Built for the barkada",
    Desserts: "Sweet endings",
    "Catering Specials": "Tailored, all in",
  };
  return map[c] || "Made with love";
}
function categoryBlurb(c) {
  const map = {
    Appetizers: "Start the celebration with crisp, savory bites and small plates that get everyone reaching across the table.",
    "Main Dishes": "The heart of every Filipino feast — slow-cooked, deeply flavored, and made to be shared.",
    "Noodles & Rice": "Long noodles for long life. Fragrant rice for a full table. The foundation of every fiesta.",
    "Party Trays": "Built for crowds. Generous trays of our most-loved dishes for your celebration.",
    Desserts: "Sweet endings — silky leche flan, coconut rice cakes, and shaved-ice classics.",
    "Catering Specials": "Full-service packages tailored to your budget, your guests, and your event.",
  };
  return map[c] || "Crafted with love by our family kitchen.";
}
